import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Button,
  Card,
  Space,
  Typography,
  Spin,
  Tag,
  Progress,
  List,
  message,
  Image,
  Upload,
} from 'antd';
import {
  RobotOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  PlayCircleOutlined,
  PictureOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
  EyeOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { SourcePanel, SourceScene } from '../../types';
import { sourceStoriesApi } from '../../services/api';
import { Tooltip } from 'antd';
import ScenePreviewModal from './ScenePreviewModal';

const { Text, Paragraph } = Typography;

const countWords = (text?: string) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).length;
};

interface BulkPanelModalProps {
  visible: boolean;
  scene: SourceScene;
  storyId: string;
  onClose: () => void;
  onRefresh?: () => void;
}

interface PanelGenerationState {
  id: string;
  status: 'pending' | 'planning' | 'refining' | 'generating' | 'setting_active' | 'completed' | 'failed';
  error?: string;
  imageUrl?: string;
  refinedPrompt?: string;
}

const BulkPanelModal: React.FC<BulkPanelModalProps> = ({
  visible,
  scene,
  storyId,
  onClose,
  onRefresh,
}) => {
  const [panels, setPanels] = useState<SourcePanel[]>([]);
  const [loading, setLoading] = useState(false);
  const [generationStates, setGenerationStates] = useState<Record<string, PanelGenerationState>>({});
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [hasStartedImages, setHasStartedImages] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [isLocalizing, setIsLocalizing] = useState(false);

  const isProcessing = isProcessingImages || isLocalizing;

  const handleAiLocalize = async () => {
    setIsLocalizing(true);
    try {
      const res = await sourceStoriesApi.aiLocalizeScenePanels(storyId, scene.id);
      if (res.data) {
        message.success(`Đã bản địa hóa xong ${res.data.length} panel`);
        fetchPanels(); // Refresh data
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      message.error('Lỗi khi bản địa hóa bằng AI');
    } finally {
      setIsLocalizing(false);
    }
  };

  const handleUpdateLocalizedCaption = async (panelId: string, value: string) => {
    try {
      await sourceStoriesApi.updateSourcePanel(storyId, scene.id, panelId, {
        localizedCaption: value
      });
      setPanels(prev => prev.map(p => p.id === panelId ? { ...p, localizedCaption: value } : p));
      message.success('Đã cập nhật nội dung bản địa hóa');
      if (onRefresh) onRefresh();
    } catch (error) {
      message.error('Không thể cập nhật nội dung bản địa hóa');
    }
  };

  // Fetch panels for the scene
  const fetchPanels = useCallback(async (silent = false) => {
    if (!visible || !scene.id) return;
    if (!silent) setLoading(true);
    setHasStartedImages(false);
    try {
      // Parallel fetch for better performance
      const [panelsRes, storyRes] = await Promise.all([
        sourceStoriesApi.getSourcePanels(storyId, scene.id),
        sourceStoriesApi.getSourceStoryDetail(storyId)
      ]);

      const scenePanels = panelsRes.data || [];
      setPanels(scenePanels);
      
      // Initialize states
      const initialStates: Record<string, PanelGenerationState> = {};
      scenePanels.forEach((p) => {
        initialStates[p.id] = {
          id: p.id,
          status: p.imagePath ? 'completed' : 'pending',
          imageUrl: p.imagePath,
        };
      });
      setGenerationStates(initialStates);
    } catch (error) {
      if (!silent) message.error('Không thể tải danh sách panel');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [visible, scene.id, storyId]);

  useEffect(() => {
    fetchPanels();
  }, [fetchPanels]);

  const updatePanelState = (panelId: string, updates: Partial<PanelGenerationState>) => {
    setGenerationStates(prev => ({
      ...prev,
      [panelId]: { ...prev[panelId], ...updates }
    }));
  };

  const processPanel = async (panel: SourcePanel, force: boolean = false): Promise<boolean> => {
    const state = generationStates[panel.id];
    if (!force && state.status === 'completed') return true;

    try {
      // Step 1: Visual Planning (AI Art Director)
      updatePanelState(panel.id, { status: 'planning', error: undefined });
      await sourceStoriesApi.checkVisualReferences(storyId, scene.id, panel.id);

      // Step 2: Refine Prompt (AI Director Script & Technical Prompt)
      updatePanelState(panel.id, { status: 'refining' });
      const refineRes = await sourceStoriesApi.refinePanelVisualPrompt(storyId, scene.id, panel.id);
      const refinedPrompt = refineRes.data.refinedPrompt;
      updatePanelState(panel.id, { refinedPrompt });

      // Step 3: Generate Image (using the stored technical prompt)
      updatePanelState(panel.id, { status: 'generating' });
      const genRes = await sourceStoriesApi.generatePanelImage(storyId, scene.id, panel.id, null); // Pass null to use refined script from backend
      const image = genRes.data.image;
      updatePanelState(panel.id, { imageUrl: image.url });

      // Step 4: Set as Active
      updatePanelState(panel.id, { status: 'setting_active' });
      await sourceStoriesApi.setPanelImage(storyId, scene.id, panel.id, image.id);
      
      updatePanelState(panel.id, { status: 'completed' });
      
      // Notify parent about the change
      if (onRefresh) {
        onRefresh();
      }
      return true;
    } catch (error: any) {
      console.error(`Failed to generate image for panel ${panel.id}:`, error);
      updatePanelState(panel.id, { 
        status: 'failed', 
        error: error.message || 'Lỗi không xác định' 
      });
      return false;
    }
  };

  const handleStartGeneration = async () => {
    setIsProcessingImages(true);
    setHasStartedImages(true);
    
    try {
      const pendingPanels = panels.filter(p => generationStates[p.id].status === 'pending' || generationStates[p.id].status === 'failed');
      
      // Sequential processing (One by one) to ensure continuity
      for (const panel of pendingPanels) {
        const success = await processPanel(panel);
        if (!success) {
          message.error(`Dừng quá trình tạo ảnh hàng loạt do lỗi tại Panel #${panel.sceneOrderIndex}`);
          break; 
        }
      }
      message.success('Quá trình tạo ảnh hàng loạt đã hoàn tất!');
      fetchPanels(true); // Silent refresh to update panels.imagePath for preview
    } finally {
      setIsProcessingImages(false);
    }
  };

  const handleRetry = (panel: SourcePanel) => {
    if (isProcessing) return;
    
    // Safety check for dependency
    const panelIndex = panels.findIndex(p => p.id === panel.id);
    if (panelIndex > 0) {
      const prevPanel = panels[panelIndex - 1];
      if (generationStates[prevPanel.id].status !== 'completed') {
        message.warning('Cần hoàn thành panel trước đó để đảm bảo tính tiếp nối');
        return;
      }
    }

    setIsProcessingImages(true);
    processPanel(panel).finally(() => {
      setIsProcessingImages(false);
      fetchPanels(true);
    });
  };

  const handleRegen = (panel: SourcePanel) => {
    if (isProcessingImages) return;
    
    // Safety check for dependency
    const panelIndex = panels.findIndex(p => p.id === panel.id);
    if (panelIndex > 0) {
      const prevPanel = panels[panelIndex - 1];
      if (generationStates[prevPanel.id].status !== 'completed') {
        message.warning('Cần hoàn thành panel trước đó để đảm bảo tính tiếp nối');
        return;
      }
    }

    setIsProcessingImages(true);
    processPanel(panel, true).finally(() => {
      setIsProcessingImages(false);
      fetchPanels(true);
    });
  };

  const handleUpload = async (panel: SourcePanel, file: File) => {
    try {
      setIsProcessingImages(true);
      const res = await sourceStoriesApi.uploadPanelImage(storyId, scene.id, panel.id, file);
      message.success('Upload ảnh thành công');
      updatePanelState(panel.id, { 
        status: 'completed', 
        imageUrl: res.data.panel.imagePath || undefined,
        error: undefined
      });
      fetchPanels(true);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      message.error('Upload ảnh thất bại: ' + (error.message || 'Unknown error'));
    } finally {
      setIsProcessingImages(false);
    }
  };

  const handleDeletePanel = (panel: SourcePanel) => {
    if (isProcessing) return;

    Modal.confirm({
      title: 'Xác nhận xóa Panel',
      icon: <ExclamationCircleOutlined />,
      content: `Bạn có chắc chắn muốn xóa Panel #${panel.sceneOrderIndex}? Các panel phía sau sẽ tự động được đánh lại số thứ tự.`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          setLoading(true);
          await sourceStoriesApi.deleteSourcePanel(storyId, scene.id, panel.id);
          message.success('Đã xóa panel thành công');
          if (onRefresh) {
            onRefresh();
          }
          fetchPanels(); // Refresh the list and re-initialize states
        } catch (error) {
          message.error('Không thể xóa panel');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const getStatusDisplay = (panelId: string) => {
    const state = generationStates[panelId];
    if (!state) return null;

    switch (state.status) {
      case 'pending':
        return <Tag icon={<SyncOutlined spin />} color="default">Đang chờ</Tag>;
      case 'planning':
        return <Tag icon={<RobotOutlined spin />} color="processing">Đang lập kế hoạch...</Tag>;
      case 'refining':
        return <Tag icon={<RobotOutlined spin />} color="processing">Đang viết kịch bản...</Tag>;
      case 'generating':
        return <Tag icon={<SyncOutlined spin />} color="processing">Đang tạo ảnh...</Tag>;
      case 'setting_active':
        return <Tag icon={<SyncOutlined spin />} color="processing">Đang lưu...</Tag>;
      case 'completed':
        return <Tag icon={<CheckCircleOutlined />} color="success">Đã xong</Tag>;
      case 'failed':
        return <Tag icon={<CloseCircleOutlined />} color="error">Lỗi</Tag>;
      default:
        return null;
    }
  };

  const totalCount = panels.length;
  
  const completedImagesCount = Object.values(generationStates).filter(s => s.status === 'completed').length;
  const progressPercentImages = totalCount > 0 ? Math.round((completedImagesCount / totalCount) * 100) : 0;

  const hasVideoPanel = panels.some(p => p.mediaType === 'VIDEO');

  const totalWords = panels.reduce((acc, panel) => {
    return acc + countWords(panel.localizedCaption || panel.caption);
  }, 0);

  const isReadyForPreview = panels.length > 0 && panels.every(p => {
    const state = generationStates[p.id];
    return state?.status === 'completed';
  });

  return (
    <Modal
      title={
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Space>
            <RobotOutlined />
            <span>Quản lý Panel - Scene: {scene.title}</span>
          </Space>
          <div style={{ fontSize: '12px', fontWeight: 'normal', color: '#8c8c8c', marginTop: 4, marginLeft: 28 }}>
            <Space split={<span style={{ color: '#d9d9d9' }}>|</span>}>
              <span>{panels.length} panel</span>
              <span>Tổng: {totalWords} từ</span>
            </Space>
          </div>
        </div>
      }
      open={visible}
      onCancel={isProcessing ? undefined : onClose}
      footer={[
        <div key="footer-warning" style={{ float: 'left', textAlign: 'left', color: '#faad14', paddingTop: 8 }}>
          <Text type="warning" style={{ fontSize: '12px' }}>
            <ExclamationCircleOutlined /> Lưu ý: Hãy review kỹ nội dung text (storyteller & caption) trước khi tạo ảnh.
          </Text>
        </div>,
        <Space key="footer-actions">
          <Button key="close" onClick={onClose} disabled={isProcessing}>
            Đóng
          </Button>
          <Tooltip title="AI sẽ tự động dịch và bản địa hóa tất cả caption trong scene này dựa trên thông tin nhân vật">
            <Button
              key="localize"
              icon={<RobotOutlined />}
              onClick={handleAiLocalize}
              loading={isLocalizing}
              disabled={panels.length === 0 || isProcessingImages}
            >
              AI Bản địa hóa
            </Button>
          </Tooltip>
          {!isProcessing && (
            <Tooltip title={!isReadyForPreview ? "Cần hoàn thành tất cả ảnh để xem preview" : ""}>
              <Button 
                key="preview" 
                icon={<EyeOutlined />} 
                onClick={() => setIsPreviewVisible(true)}
                disabled={!isReadyForPreview}
              >
                Xem Preview
              </Button>
            </Tooltip>
          )}
          {!isProcessingImages && panels.some(p => generationStates[p.id]?.status !== 'completed') && (
            <Tooltip title={
              hasVideoPanel 
                ? "Scene có chứa Video Panel. Hiện tại chưa hỗ trợ tạo hàng loạt cho Video. Vui lòng xử lý thủ công từng panel."
                : (panels.some(p => !(p.localizedCaption?.trim() || p.caption?.trim())) ? "Tất cả các panel phải có nội dung caption trước khi tạo ảnh hàng loạt" : "")
            }>
              <Button 
                key="start" 
                type="primary" 
                icon={<PlayCircleOutlined />} 
                onClick={handleStartGeneration}
                loading={isProcessingImages}
                disabled={panels.length === 0 || panels.some(p => !(p.localizedCaption?.trim() || p.caption?.trim())) || hasVideoPanel}
              >
                {completedImagesCount > 0 ? 'Tạo ảnh cho các panel còn lại' : 'Bắt đầu tạo ảnh hàng loạt'}
              </Button>
            </Tooltip>
          )}
        </Space>
      ]}
      width={1100}
      centered
      maskClosable={false}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: '24px'
        }
      }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {hasStartedImages && (
            <Card size="small" style={{ background: '#f6ffed' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong>Tiến độ tạo ảnh</Text>
                <Text strong>{completedImagesCount} / {totalCount} đã hoàn thành</Text>
              </div>
              <Progress percent={progressPercentImages} status={isProcessingImages ? "active" : "normal"} strokeColor="#52c41a" />
            </Card>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin tip="Đang tải danh sách panel..." />
          </div>
        ) : (
          <List
            dataSource={panels}
            renderItem={(panel) => {
              const state = generationStates[panel.id];
              const borderColor = state?.status === 'completed' ? '#52c41a' : 
                                 state?.status === 'failed' ? '#ff4d4f' : 
                                 '#1890ff';
              
              const panelIndex = panels.findIndex(p => p.id === panel.id);
              const isFirstPanel = panelIndex === 0;
              const prevPanelCompleted = isFirstPanel || (panelIndex > 0 && generationStates[panels[panelIndex - 1].id]?.status === 'completed');
              const hasCaption = !!(panel.localizedCaption?.trim() || panel.caption?.trim());
              const canGenerate = (isFirstPanel || prevPanelCompleted) && hasCaption;

              const getTooltipTitle = () => {
                if (!hasCaption) return "Cần nội dung caption hoặc bản địa hóa trước khi tạo ảnh";
                if (!prevPanelCompleted) return "Cần hoàn thành panel trước đó để đảm bảo tính tiếp nối";
                return "";
              };

              return (
                <List.Item style={{ padding: '4px 0' }}>
                  <div 
                    style={{ 
                      width: '100%', 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '8px 12px',
                      background: '#fff',
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      borderLeft: `4px solid ${borderColor}`,
                    }}
                  >
                    {/* Image Thumbnail */}
                    <div style={{ 
                      width: '60px', 
                      height: '106px', // 9:16 aspect ratio
                      flexShrink: 0,
                      background: '#f0f0f0', 
                      borderRadius: 4, 
                      overflow: 'hidden',
                      border: '1px solid #d9d9d9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {state?.imageUrl ? (
                        <Image 
                          src={state.imageUrl} 
                          alt="Result" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                          preview={{
                            mask: <div style={{ fontSize: 12 }}>Xem</div>
                          }}
                        />
                      ) : state?.status === 'generating' || state?.status === 'refining' ? (
                        <Spin size="small" />
                      ) : (
                        <PictureOutlined style={{ fontSize: 20, color: '#bfbfbf' }} />
                      )}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <Space>
                          <Tag color="blue" style={{ margin: 0 }}>Panel #{panel.sceneOrderIndex}</Tag>
                          {panel.mediaType === 'VIDEO' ? (
                            <Tag color="purple" icon={<VideoCameraOutlined />} style={{ margin: 0 }}>VIDEO</Tag>
                          ) : (
                            <Tag color="cyan" icon={<PictureOutlined />} style={{ margin: 0 }}>IMAGE</Tag>
                          )}
                          <Tag color="default" style={{ margin: 0 }}>{countWords(panel.localizedCaption || panel.caption)} từ</Tag>
                          {panel.estimatedAudioDuration && (
                            <Tag color="orange" style={{ margin: 0 }}>~{panel.estimatedAudioDuration}s audio</Tag>
                          )}
                        </Space>
                        <Space>
                          {getStatusDisplay(panel.id)}
                        </Space>
                      </div>
                      
                      <div style={{ 
                        padding: '4px 8px', 
                        background: '#f9f9f9', 
                        borderRadius: 4, 
                        fontSize: '12px',
                        border: '1px solid #f0f0f0',
                        lineHeight: '1.4'
                      }}>
                        <Paragraph 
                          italic 
                          style={{ color: '#555', marginBottom: 0 }}
                          ellipsis={{ rows: 2, tooltip: panel.caption }}
                        >
                          "{panel.caption}"
                        </Paragraph>
                      </div>

                      <div style={{ marginTop: 4 }}>
                        <Paragraph
                          editable={{
                            onChange: (value) => handleUpdateLocalizedCaption(panel.id, value),
                            tooltip: 'Bấm để sửa nội dung bản địa hóa',
                            triggerType: ['text', 'icon']
                          }}
                          style={{ 
                            color: panel.localizedCaption ? '#52c41a' : '#faad14', 
                            marginBottom: 0, 
                            fontSize: '11px',
                            background: panel.localizedCaption ? '#f6ffed' : '#fffbe6',
                            padding: '2px 8px',
                            borderRadius: 4,
                            border: panel.localizedCaption ? '1px dashed #b7eb8f' : '1px dashed #ffe58f'
                          }}
                        >
                          {panel.localizedCaption || 'Chưa bản địa hóa (Bấm để sửa...)'}
                        </Paragraph>
                      </div>

                      {state?.error && (
                        <div style={{ marginTop: 4 }}>
                          <Text type="danger" style={{ fontSize: '11px' }} ellipsis={{ tooltip: state.error }}>
                            <CloseCircleOutlined /> Lỗi: {state.error}
                          </Text>
                        </div>
                      )}
                    </div>

                    {/* Action */}
                    {(state?.status === 'failed' || state?.status === 'completed' || state?.status === 'pending') && (
                      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Tooltip title={getTooltipTitle()}>
                          <Button 
                            type={state?.status === 'completed' ? "default" : "primary"}
                            size="small" 
                            icon={<SyncOutlined />} 
                            onClick={() => state?.status === 'failed' ? handleRetry(panel) : handleRegen(panel)}
                            disabled={isProcessing || !canGenerate}
                            style={{ width: '100%', fontSize: '11px' }}
                          >
                            {state?.status === 'failed' ? 'Thử lại ảnh' : 
                             state?.status === 'pending' ? 'Tạo ảnh' : 'Tạo lại ảnh'}
                          </Button>
                        </Tooltip>

                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          beforeUpload={(file) => {
                            handleUpload(panel, file);
                            return false;
                          }}
                          disabled={isProcessing}
                        >
                          <Button 
                            size="small" 
                            icon={<UploadOutlined />} 
                            disabled={isProcessing}
                            style={{ width: '100%', fontSize: '11px' }}
                          >
                            Upload ảnh
                          </Button>
                        </Upload>

                        <Button 
                          type="text" 
                          danger 
                          size="small" 
                          icon={<DeleteOutlined />} 
                          onClick={() => handleDeletePanel(panel)}
                          disabled={isProcessing}
                          style={{ width: '100%', fontSize: '11px' }}
                        >
                          Xóa panel
                        </Button>
                      </div>
                    )}
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Space>

      <ScenePreviewModal
        visible={isPreviewVisible}
        scene={scene}
        panels={panels}
        onClose={() => setIsPreviewVisible(false)}
      />

    </Modal>
  );
};

export default BulkPanelModal;
